!function () {
  "use strict";

  class CartBar extends HTMLElement {
    constructor() {
      super();

      this.boundHandleClick = this.handleClick.bind(this);
      this.boundResetATC = this.resetATC.bind(this);
      this.boundOptionChange = this.handleOptionChange.bind(this);
      this.boundScroll = this.scheduleVisibilityUpdate.bind(this);
      this.boundResize = this.scheduleVisibilityUpdate.bind(this);
      this.scrollRaf = null;
      this.visibilityObserver = null;
    }

    connectedCallback() {
      this.section = this.closest("product-info");
      this.form = this.section?.querySelector("[data-product-form]");

      if (!this.section || !this.form) return;

      this.formWrapper =
        (window.theme?.settings?.productPageSticky && this.section.querySelector("[data-form-wrapper]")) ||
        this.form;
      this.triggerButton = this.form.querySelector("[data-add-to-cart]") || this.formWrapper;
      this.image = this.querySelector("[data-cart-bar-image]");
      this.options = this.querySelector("[data-cart-bar-options]");
      this.qty = this.querySelector("[data-cart-bar-qty]");
      this.atcBtn = this.querySelector("[data-cart-bar-add-to-cart]");

      this.addEventListener("click", this.boundHandleClick);
      ["theme:cart:refresh", "theme:cart-bar:reset-atc-animation"].forEach((eventName) => {
        document.addEventListener(eventName, this.boundResetATC);
      });

      this.initOptions();
      this.initQty();
      this.subVariant();
      this.initVisibilityTracking();
    }

    initVisibilityTracking() {
      this.updateVisibility();

      if ("IntersectionObserver" in window) {
        this.visibilityObserver = new IntersectionObserver(
          ([entry]) => {
            const mainButtonIsVisible =
              entry.isIntersecting &&
              entry.intersectionRect.width > 0 &&
              entry.intersectionRect.height > 0;

            this.classList.toggle("is-visible", !mainButtonIsVisible);
          },
          { threshold: 0 }
        );
        this.visibilityObserver.observe(this.triggerButton);
      }

      document.addEventListener("theme:scroll", this.boundScroll);
      document.addEventListener("theme:resize", this.boundResize);
      window.addEventListener("resize", this.boundResize);
    }

    scheduleVisibilityUpdate() {
      if (this.scrollRaf) return;

      this.scrollRaf = requestAnimationFrame(() => {
        this.scrollRaf = null;
        this.updateVisibility();
      });
    }

    updateVisibility() {
      if (!this.triggerButton) return;

      const triggerRect = this.triggerButton.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const mainButtonIsVisible =
        triggerRect.width > 0 &&
        triggerRect.height > 0 &&
        triggerRect.bottom > 0 &&
        triggerRect.right > 0 &&
        triggerRect.top < viewportHeight &&
        triggerRect.left < viewportWidth;

      this.classList.toggle("is-visible", !mainButtonIsVisible);
    }

    resetATC() {
      this.atcBtn?.classList.remove("is-loading");
      this.atcBtn?.removeAttribute("disabled");
    }

    handleClick(event) {
      if (event.target.closest("[data-scroll-to-form]")) {
        event.preventDefault();

        const productForm = this.section.querySelector("product-form");

        if (!productForm) return;

        productForm.setAttribute("tabindex", "-1");
        productForm.focus({ preventScroll: !0 });
        productForm.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }

      if (event.target.closest("[data-cart-bar-add-to-cart]")) {
        event.preventDefault();

        const submitButton = this.form.querySelector("[data-add-to-cart]");

        if (!submitButton || submitButton.hasAttribute("disabled")) return;

        this.atcBtn.classList.add("is-loading");
        this.atcBtn.setAttribute("disabled", !0);

        if (this.form.requestSubmit) {
          this.form.requestSubmit(submitButton);
        } else {
          submitButton.click();
        }
      }
    }

    initOptions() {
      this.options
        ?.querySelectorAll("[data-popout-input]")
        .forEach((input) => input.addEventListener("change", this.boundOptionChange));
    }

    handleOptionChange(event) {
      const optionValueId = event.target.getAttribute("data-option-value-id");
      if (!optionValueId) return;

      const input = this.section.querySelector(
        `variant-selects [data-option-value-id="${CSS.escape(optionValueId)}"]`
      );

      if (!input) return;

      const clickEvent = new MouseEvent("click", { bubbles: !0, cancelable: !0 });
      clickEvent.fromCartBar = !0;

      if (input.tagName === "A" || input.matches("input")) {
        input.dispatchEvent(clickEvent);

        if (input.matches("input")) {
          const changeEvent = new Event("change", { bubbles: !0 });
          changeEvent.fromCartBar = !0;
          input.dispatchEvent(changeEvent);
        }
      } else {
        input.closest('label, a, [role="button"], .select-popout__option')?.dispatchEvent(clickEvent);
      }
    }

    subVariant() {
      if (!window.subscribe || !window.theme?.PUB_SUB_EVENTS?.variantChange) return;

      this.unsub = window.subscribe(
        window.theme.PUB_SUB_EVENTS.variantChange,
        ({ data }) => {
          const sectionId = data?.sectionId;
          if (!sectionId || (sectionId !== this.section.dataset.section && sectionId !== this.section.dataset.originalSection)) {
            return;
          }

          this.updateImage(data.variant);
          this.updateOptions(data.variant);
          this.updateBtn(data.variant);
          this.updatePrice(data.variant);
        }
      );
    }

    updateImage(variant) {
      const image = this.image?.querySelector("img");
      const src = variant?.featured_media?.preview_image?.src;

      if (image && src) {
        image.src = `${src}${src.includes("?") ? "&" : "?"}width=160`;
      }
    }

    updateOptions(variant) {
      if (!this.options || !variant?.options) return;

      this.options.querySelectorAll("popout-select").forEach((select) => {
        const input = select.querySelector("[data-popout-input]");
        const index = parseInt(input?.dataset.index?.replace("option", "") || "", 10);

        if (!input || Number.isNaN(index)) return;

        const selectedValue = variant.options[index - 1];
        const text = select.querySelector("[data-popout-text]");

        select.querySelectorAll("[data-popout-option]").forEach((option) => {
          const value = option.dataset.value;
          const isCurrent = value === selectedValue;
          const item = option.closest("li");

          if (isCurrent && item) {
            if (text) text.textContent = value;
            input.value = value;
            if (item.dataset.optionValueId) {
              input.setAttribute("data-option-value-id", item.dataset.optionValueId);
            }
            if (item.dataset.productUrl) {
              input.setAttribute("data-product-url", item.dataset.productUrl);
            }
            select.querySelector(".select-popout__item--current")?.classList.remove("select-popout__item--current");
            item.classList.add("select-popout__item--current");
            select.querySelector('[aria-current="true"]')?.removeAttribute("aria-current");
            option.setAttribute("aria-current", "true");
          }

          const optionValueId = item?.dataset.optionValueId;
          if (!optionValueId) return;

          const source = this.section.querySelector(
            `variant-selects [data-option-value-id="${CSS.escape(optionValueId)}"]`
          );

          if (!source) return;

          const unavailable =
            source.closest(".sold-out, .unavailable") ||
            source.classList.contains("unavailable") ||
            source.disabled;

          option.classList.toggle("unavailable", unavailable);
        });
      });
    }

    updateBtn(variant) {
      const button = this.atcBtn;
      const label = this.querySelector("[data-add-to-cart-text]");

      if (!button || !label || !variant) return;

      const available = variant.available;
      label.textContent =
        window.theme?.strings?.[available ? "addToCart" : "soldOut"] ||
        (available ? "Add to cart" : "Sold out");
      button.toggleAttribute("disabled", !available);
    }

    updatePrice(variant) {
      const price = this.querySelector("[data-product-price]");
      if (!price || !variant) return;

      const formatMoney = (value) =>
        window.theme.formatMoney(
          value,
          window.theme?.settings?.currency_code_enable
            ? window.theme.moneyWithCurrencyFormat
            : window.theme.moneyFormat
        );

      price.innerHTML = variant.price === 0 ? window.theme?.strings?.free || "Free" : formatMoney(variant.price);

      const isOnSale = variant.compare_at_price > variant.price;
      price.classList.toggle("product__price--sale", isOnSale);

      const strike = this.querySelector(".product__price--strike");
      if (strike) {
        strike.textContent = isOnSale ? formatMoney(variant.compare_at_price) : "";
        strike.classList.toggle("hidden", !isOnSale);
      }
    }

    initQty() {
      if (!this.qty) return;

      const barQty = this.qty.querySelector("[data-quantity-field]");
      const mainQty = Array.from(this.section.querySelectorAll("[data-quantity-holder]"))
        .find((holder) => !this.contains(holder))
        ?.querySelector("[data-quantity-field]");

      if (!barQty || !mainQty) return;

      barQty.value = mainQty.value;
      this.qtySync = !1;

      const sync = (source, target) => {
        if (this.qtySync) return;
        this.qtySync = !0;
        target.value = source.value;
        target.dispatchEvent(new Event("change", { bubbles: !0 }));
        this.qtySync = !1;
      };

      this.syncMain = () => sync(mainQty, barQty);
      this.syncBar = () => sync(barQty, mainQty);

      [mainQty, barQty].forEach((field) => {
        ["input", "change"].forEach((eventName) => {
          field.addEventListener(eventName, field === mainQty ? this.syncMain : this.syncBar);
        });
      });

      this.mainQtyRef = mainQty;
      this.barQtyRef = barQty;
    }

    disconnectedCallback() {
      ["theme:cart:refresh", "theme:cart-bar:reset-atc-animation"].forEach((eventName) => {
        document.removeEventListener(eventName, this.boundResetATC);
      });

      document.removeEventListener("theme:scroll", this.boundScroll);
      document.removeEventListener("theme:resize", this.boundResize);
      window.removeEventListener("resize", this.boundResize);
      this.visibilityObserver?.disconnect();

      this.unsub?.();
      this.removeEventListener("click", this.boundHandleClick);
      this.options?.querySelectorAll("[data-popout-input]").forEach((input) => {
        input.removeEventListener("change", this.boundOptionChange);
      });

      if (this.scrollRaf) {
        cancelAnimationFrame(this.scrollRaf);
        this.scrollRaf = null;
      }

      this.mainQtyRef && ["input", "change"].forEach((eventName) => {
        this.mainQtyRef.removeEventListener(eventName, this.syncMain);
      });

      this.barQtyRef && ["input", "change"].forEach((eventName) => {
        this.barQtyRef.removeEventListener(eventName, this.syncBar);
      });
    }
  }

  customElements.get("cart-bar") || customElements.define("cart-bar", CartBar);
}();
